/**
 * Modal de resumen de consumos por marca.
 *
 * Responsabilidades:
 * - Mostrar todas las combinaciones disponibles de:
 *   familia, año, catálogo y consumo (si existe) para una marca.
 * - Permitir buscar de forma global por familia, año o catálogo
 *   mediante un input de texto.
 * - Mantener el encabezado de la tabla fijo (sticky) mientras el
 *   contenido se desplaza.
 * - Mostrar contadores de:
 *   - total de combinaciones
 *   - cuántas tienen consumo registrado.
 *
 * Props:
 * - marca: nombre de la marca a la que pertenece el resumen.
 * - rows: combinaciones [{ familia, ano_modelo, catalogo_codigo,
 *   catalogoLabel?, modeloLabel?, consumo_km_l? }].
 * - onClose(): cierra el modal.
 */


import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';

const ConsumoResumenModal = ({ marca, rows, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    requestAnimationFrame(() => setVisible(true));
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 300);
  };

  const filteredRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((row) => {
      return (
        (row.familia || '').toLowerCase().includes(q) ||
        String(row.ano_modelo || '').toLowerCase().includes(q) ||
        (row.catalogo_codigo || '').toLowerCase().includes(q) ||
        (row.catalogoLabel || '').toLowerCase().includes(q) ||
        (row.modeloLabel || '').toLowerCase().includes(q)
      );
    });
  }, [rows, searchTerm]);

  const modalContent = (
    <div
      className={`fixed inset-0 z-[9999] transition-colors duration-300 backdrop-blur-sm ${visible ? 'bg-black/60' : 'bg-black/0'}`}
      onClick={handleClose}
    >
      {/* Panel — bottom-sheet on mobile, centered modal on desktop */}
      <div
        className={`fixed z-[10000] flex flex-col bg-white overflow-hidden
          bottom-0 left-0 right-0 rounded-t-2xl max-h-[92vh]
          sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:right-auto
          sm:-translate-x-1/2 sm:-translate-y-1/2
          sm:w-[95%] sm:max-w-5xl sm:rounded-xl sm:shadow-2xl sm:max-h-[85vh]
          transition-all duration-300 ease-out
          ${visible ? 'translate-y-0 opacity-100' : 'translate-y-full sm:translate-y-[-48%] opacity-0 pointer-events-none'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex-shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-base sm:text-2xl font-bold text-gray-900 leading-tight">
                Consumos registrados – {marca}
              </h3>
              <p className="text-xs sm:text-base text-gray-500 mt-0.5 sm:mt-1">
                Todas las combinaciones de familias, años y catálogos disponibles
              </p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="w-8 h-8 sm:w-12 sm:h-12 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors shrink-0"
            >
              <svg className="w-5 h-5 sm:w-7 sm:h-7" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>

        {/* Buscador */}
        <div className="px-4 sm:px-6 pt-3 flex-shrink-0">
          <div className="max-w-md">
            <input
              type="text"
              placeholder="Buscar por familia, año o catálogo..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm.trim() && (
              <p className="mt-1 text-xs text-gray-500">
                Mostrando {filteredRows.length} de {rows.length} combinaciones
              </p>
            )}
          </div>
        </div>

        {/* Contenido - SCROLL AQUÍ */}
        <div className="mx-3 sm:mx-6 my-3 sm:my-4 border border-gray-200 rounded-lg shadow-sm flex-1 overflow-auto bg-white min-h-0">
          {rows.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-sm sm:text-base text-gray-500">
                No hay combinaciones disponibles para esta marca.
              </p>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-sm sm:text-base text-gray-500">
                No se encontraron resultados para "{searchTerm}".
              </p>
            </div>
          ) : (
            <table className="min-w-full">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 sm:px-8 py-3 sm:py-5 text-left text-xs sm:text-base font-bold text-gray-700 uppercase tracking-wider">
                    Familia / Año
                  </th>
                  <th className="px-4 sm:px-8 py-3 sm:py-5 text-left text-xs sm:text-base font-bold text-gray-700 uppercase tracking-wider">
                    Catálogo
                  </th>
                  <th className="px-4 sm:px-8 py-3 sm:py-5 text-center text-xs sm:text-base font-bold text-gray-700 uppercase tracking-wider">
                    Consumo (km/l)
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {filteredRows.map((row, idx) => (
                  <tr
                    key={`${row.familia}-${row.ano_modelo}-${row.catalogo_codigo}-${idx}`}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td className="px-4 sm:px-8 py-3 sm:py-4 text-sm sm:text-base text-gray-900">
                      {row.familia} {row.ano_modelo}
                    </td>
                    <td className="px-4 sm:px-8 py-3 sm:py-4 text-sm sm:text-base text-gray-900">
                      {row.catalogo_codigo}
                    </td>
                    <td className="px-4 sm:px-8 py-3 sm:py-4 text-center">
                      {row.consumo_km_l != null ? (
                        <span className="inline-flex items-center px-2.5 sm:px-4 py-1 sm:py-2 rounded-full bg-orange-50 text-orange-700 text-xs sm:text-base font-semibold">
                          {row.consumo_km_l} km/l
                        </span>
                      ) : (
                        <span className="text-xs sm:text-base text-gray-400 italic">
                          Sin registro
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex-shrink-0">
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs sm:text-base text-gray-600">
              <span className="font-bold text-sm sm:text-lg">{rows.length}</span> catálogos diferentes &bull;{' '}
              <span className="font-bold text-sm sm:text-lg text-orange-600">
                {rows.filter(r => r.consumo_km_l != null).length}
              </span>{' '}con consumo registrado
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="px-4 sm:px-8 py-2.5 sm:py-4 text-sm sm:text-base font-semibold rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors shrink-0"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default ConsumoResumenModal;